import { FC, useCallback, useState } from 'react';
import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { FaSitemap } from 'react-icons/fa6';

import { Sku } from '@app/api/models/sku';
import { RequestPayload } from '@app/api/types';
import { SkuListPayload } from '@app/api/sku';
import { Dialog, Txt } from '@app/components/common';
import { QueryTable } from '@app/components/common/table';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useApi, useFetchData } from '@app/hooks';

import { useColumns } from './columns';
import { SkuForm } from './form';
import { SkuLookups } from './types';

const SkuDialog: FC<{
  open: boolean;
  sku?: Sku;
  lookups: SkuLookups;
  onClose: () => void;
}> = ({ open, sku, lookups, onClose }) => {
  const { refetch } = useQueryTable();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={sku ? 'Редактирование SKU' : 'Создание SKU'}
      width={720}
      footerless
    >
      {open && (
        <SkuForm
          initial={sku}
          lookups={lookups}
          onSubmit={() => {
            refetch();
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </Dialog>
  );
};

export const SkuLinearTable: FC<{
  lookups: SkuLookups;
  canChange: boolean;
  onToggle: () => void;
}> = ({ lookups, canChange, onToggle }) => {
  const api = useApi();
  const [selected, setSelected] = useState<Sku>();
  const [open, setOpen] = useState(false);

  const onEdit = useCallback((sku: Sku) => {
    setSelected(sku);
    setOpen(true);
  }, []);

  const onCreate = useCallback(() => {
    setSelected(undefined);
    setOpen(true);
  }, []);

  const columns = useColumns({ lookups, onEdit, canChange });

  const fetchSkus = useCallback(
    (payload: RequestPayload) =>
      api.sku.getSkus({
        limit: payload.limit,
        offset: payload.offset,
        sort: payload.sort,
        filter: payload.filter as unknown as SkuListPayload['filter'],
      }),
    [api]
  );

  const [fetchData] = useFetchData<Sku>({ method: fetchSkus });

  return (
    <QueryTable<Sku>
      title={
        <Flex gap={2} alignItems="center">
          <Txt>Управление SKU</Txt>
          <Tooltip label="Иерархическое представление">
            <IconButton aria-label="tree-view" icon={<FaSitemap />} onClick={onToggle} size="sm" />
          </Tooltip>
        </Flex>
      }
      columns={columns}
      fetchData={fetchData}
      onCreateClick={canChange ? onCreate : undefined}
    >
      <SkuDialog open={open} sku={selected} lookups={lookups} onClose={() => setOpen(false)} />
    </QueryTable>
  );
};

export default SkuLinearTable;
